// 导入样式文件
import "./styles/variables.css";
import "./styles/global.css";

import { createApp } from "vue";
import { createPinia } from "pinia";
import ElementPlus from "element-plus";
import "element-plus/dist/index.css";
import * as ElementPlusIconsVue from "@element-plus/icons-vue";
import Highcharts from "highcharts";
import HighchartsAccessibility from "highcharts/modules/accessibility";

import App from "./App.vue";
import router from "./router";
import { useUserStore, useAppStore } from "./stores";
import { checkAndRestoreAuth } from "./utils/authCheck";
import { initServices } from "./services";
import assetsPlugin from "./plugins/assets";
import { initZoomDetection } from "./utils/zoomUtils";
import { logger } from "./utils/logger";
import { setupChunkLoadRecovery } from "./utils/chunkLoadRecovery";
import accessTokenManager from "./utils/accessToken";

HighchartsAccessibility(Highcharts);

// 添加全局调试方法
declare global {
  interface Window {
    enableDebug: () => void;
    disableDebug: () => void;
    getDebugStatus: () => boolean;
  }
}

window.enableDebug = () => {
  localStorage.setItem("debug_mode", "true");
  console.log("✅ 调试模式已开启，刷新页面生效");
};

window.disableDebug = () => {
  localStorage.removeItem("debug_mode");
  console.log("❌ 调试模式已关闭，刷新页面生效");
};

window.getDebugStatus = () => {
  const isDebug = localStorage.getItem("debug_mode") === "true";
  console.log(`调试模式: ${isDebug ? "已开启 ✅" : "已关闭 ❌"}`);
  return isDebug;
};

setupChunkLoadRecovery();

// 创建应用实例
const app = createApp(App);
const pinia = createPinia();

// 注册Element Plus图标
for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
  app.component(key, component);
}

// 安装插件
app.use(pinia);
app.use(router);
app.use(ElementPlus);
app.use(assetsPlugin);

// 初始化应用
async function initApp() {
  try {
    logger.info("初始化套利宝系统...");

    // 尝试恢复用户认证状态
    const isAuthenticated = await checkAndRestoreAuth();
    if (isAuthenticated) {
      logger.info("用户认证状态恢复成功");
    }

    // 初始化用户store
    const userStore = useUserStore();
    await userStore.initFromStorage();

    // 初始化应用store
    const appStore = useAppStore();
    appStore.initWindowListener();

    // 初始化服务层
    initServices();

    // 初始化缩放检测
    initZoomDetection();

    logger.info("系统初始化完成");
  } catch (error) {
    logger.error("初始化过程中出现错误:", error);
  } finally {
    // 无论初始化是否成功，都要挂载应用
    app.mount("#app");
    logger.info("套利宝系统启动完成");
    bootstrapAnalytics();
  }
}

/**
 * 用户行为埋点（pcpMulti 页面簇）。
 * 挂载完成后在 idle 时段动态 import，独立 chunk 不进 vendor；
 * 加载或安装失败一律静默，绝不影响应用。
 */
function bootstrapAnalytics() {
  try {
    const enabled =
      import.meta.env.VITE_ENABLE_TRACK === "true" ||
      window.localStorage?.getItem("ck_track") === "1";
    if (!enabled) return;
    const idle =
      (window as any).requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 1500));
    idle(() => {
      import("./analytics")
        .then((m) =>
          m.installAnalytics(app, router, {
            // 同步只读缓存，永不触发 token refresh
            getToken: () => accessTokenManager.peekAccessToken(),
          }),
        )
        .catch(() => undefined);
    });
  } catch {
    /* 埋点永不影响启动 */
  }
}

// 启动应用
initApp();
